import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { validateNoLinks } from '../../utils/validation';
import { useTranslation } from 'react-i18next';

interface Project {
  id: string;
  name: string;
  notes?: string;
}

const ProjectsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({ name: '', notes: '' });
  const [notesError, setNotesError] = useState<string>('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get('/reference/projects');
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const handleOpenDialog = (project?: Project) => {
    if (project) {
      setEditingProject(project);
      setFormData({ name: project.name, notes: project.notes || '' });
    } else {
      setEditingProject(null);
      setFormData({ name: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    // Validate notes for links
    const notesValidation = validateNoLinks(formData.notes);
    if (!notesValidation.isValid) {
      setNotesError(notesValidation.error || '');
      return;
    }
    setNotesError('');

    try {
      if (editingProject) {
        await api.put(`/reference/projects/${editingProject.id}`, formData);
      } else {
        await api.post('/reference/projects', formData);
      }
      setDialogOpen(false);
      loadProjects();
    } catch (err) {
      console.error('Failed to save project:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.deleteProjectConfirm'))) return;
    try {
      await api.delete(`/reference/projects/${id}`);
      loadProjects();
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        {t('admin.addProject')}
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('admin.notes')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.name}</TableCell>
                <TableCell>{project.notes || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(project)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(project.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProject ? t('admin.editProject') : t('admin.addProject')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('common.name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('admin.notes')}
            value={formData.notes}
            onChange={(e) => {
              setFormData({ ...formData, notes: e.target.value });
              const validation = validateNoLinks(e.target.value);
              setNotesError(validation.error || '');
            }}
            error={!!notesError}
            helperText={notesError || t('admin.linksNotAllowed')}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectsManagement;

